import { Helmet } from 'react-helmet-async'

import {
  Box, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import useAgency from '@hooks/useAgency'
import useBrand from '@hooks/useBrand'
import useCampaign from '@hooks/useCampaign'
import useImages from '@hooks/useImages'

import NotificationBannerLocation from '@components/NotificationBannerLocation'

import AgencyAndAgentsDetails from '@sections/Public/packs/Campaign/components/AgencyAndAgentsDetails'
import AgencyContactInfo from '@sections/Public/packs/Campaign/components/AgencyContactInfo'
import Footer from '@sections/Public/packs/Campaign/components/Footer'
import Header from '@sections/Public/packs/Campaign/components/Header'

import Inspections from './Inspections'
import MediaAndAdCopy from './MediaAndAdCopy'

interface PageContentProps {
  adCopyTypes: string[],
  campaignId: number,
  imageIds: number[],
}

const PageContent = (props: PageContentProps) => {
  const { adCopyTypes, campaignId, imageIds } = props

  const campaignPayload = useCampaign({ id: campaignId })
  const {
    callbacks: {
      downloadMedia,
    },
    campaign: {
      agency_id,
      agent_ids,
      team_id,
    },
    campaign,
    loading,
  } = campaignPayload

  const agencyPayload = useAgency({ id: agency_id })
  const { agency, agency: { brand_id } } = agencyPayload

  const brandPayload = useBrand({ id: brand_id })
  const { brand, publicLinkTextAccentColour } = brandPayload
  // @ts-expect-error TS2339
  const brandPublicLinkCoverImageId = brand?.public_link_cover_image_id || null
  // @ts-expect-error TS2339
  const brandPublicLinkLogoId = brand?.public_link_logo_id || null
  const brandLegalDisclaimer = brand?.options?.branded_public_links?.legal_disclaimer || undefined
  const brandShowOFIs = brand?.options?.branded_public_links?.show_inspections || false

  const imagesPayload = useImages({ filters: { image_ids: imageIds } })
  const { filteredImages, hasFilteredImages } = imagesPayload

  return (
    <>
      <Helmet>
        <title>Media Reviewer | Realhub</title>
      </Helmet>

      <Header campaignPayload={campaignPayload} coverImageId={brandPublicLinkCoverImageId} />

      <Box
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
        marginTop={[-32, -64]}
        marginBottom="large"
        paddingX="large"
        position="relative"
        zIndex="10"
      >
        <Columns flexDirection={['column', 'column', 'row']} boxProps={{ maxWidth: ['100%', '900px'] }}>
          <Columns.Main marginRight="none" width={['100%', '900px']}>
            <LoadingModule loading={loading} times={3} />

            <NotificationBannerLocation
              locationKey="agency.mediaReviewer.campaignPublic.banner"
            />

            <AgencyAndAgentsDetails
              agencyId={agency_id}
              agentIds={agent_ids || []}
              brandId={brand_id}
              logoId={brandPublicLinkLogoId}
              teamId={team_id}
            />
          </Columns.Main>
        </Columns>
      </Box>

      {brandShowOFIs && (
        <Box width="100%" justifyContent="center">
          <Box
            alignItems="center"
            flexDirection="column"
            justifyContent="center"
            width={['100%', '900px']}
            marginBottom="large"
            position="relative"
            zIndex="10"
            border="1px solid"
            borderColor="lineColor"
            borderRadius={5}
          >
            <Inspections accentColor={publicLinkTextAccentColour} campaign={campaign} />
          </Box>
        </Box>
      )}

      {(hasFilteredImages || adCopyTypes?.length > 0) && (
        <Box
          alignItems="center"
          backgroundColor="backgroundColor"
          flexDirection="column"
          justifyContent="center"
          paddingX="large"
          position="relative"
          zIndex="10"
        >
          <Columns
            flexDirection={['column', 'column', 'row']}
            boxProps={{ maxWidth: ['100%', '900px'], paddingTop: 'large' }}
          >
            <Columns.Main marginRight="none" width={['100%', '900px']}>
              <MediaAndAdCopy
                brandId={brand_id}
                callbacks={{ downloadMedia }}
                campaignId={campaignId}
                filteredImages={filteredImages}
                hasFilteredImages={hasFilteredImages}
                queryImageIds={imageIds}
                sharedAdCopyTypes={adCopyTypes}
              />
            </Columns.Main>
          </Columns>
        </Box>
      )}

      {/* @ts-expect-error */}
      <AgencyContactInfo agency={agency} />

      <Footer disclaimer={brandLegalDisclaimer} />
    </>
  )
}

export default PageContent
